import { useState, useEffect } from 'react';
import { FileStat, FolderSizeResult, ChildSizeNode } from '../../api/IFileSystem';
import { useFileManagerStore } from '../../store/useFileManagerStore';

export interface TreemapNode {
  name: string;
  path: string;
  size: number; // bytes, drives area
  isDir: boolean;
  fileCount?: number;
  isOther?: boolean; // aggregated "others" tile (not navigable)
}

// Only render the N biggest tiles; the rest collapse into one "others" block,
// matching how the reference tool avoids thousands of 1px slivers.
const TOP_N = 80;
const BATCH = 200;

interface TreemapData {
  nodes: TreemapNode[];
  total: number; // sum of all valid sizes (pre-truncation) for percentage
  loading: boolean;
  progress: number; // 0-100
}

function finalize(nodes: TreemapNode[]): { nodes: TreemapNode[]; total: number } {
  const valid = nodes.filter((n) => n.size > 0).sort((a, b) => b.size - a.size);
  const total = valid.reduce((s, n) => s + n.size, 0);
  if (valid.length <= TOP_N) return { nodes: valid, total };

  const head = valid.slice(0, TOP_N);
  const rest = valid.slice(TOP_N);
  const otherSize = rest.reduce((s, n) => s + n.size, 0);
  head.push({
    name: `其他 (${rest.length})`,
    path: '__other__',
    size: otherSize,
    isDir: false,
    isOther: true,
  });
  return { nodes: head, total };
}

/**
 * Build treemap nodes for the current directory.
 * - root: one tile per drive, area = used space (getVolumeSize)
 * - directory: subfolders sized via getFolderSizes (folder_stats cache),
 *   files sized via their own size from the listing.
 */
export function useTreemapData(currentPath: string, files: FileStat[]): TreemapData {
  const fs = useFileManagerStore((s) => s.fs);
  const [data, setData] = useState<TreemapData>({ nodes: [], total: 0, loading: false, progress: 0 });

  useEffect(() => {
    let cancelled = false;

    const build = async () => {
      if (!fs) return;
      setData((d) => ({ ...d, loading: true, progress: 0 }));

      const isRoot = currentPath === 'root';

      if (isRoot) {
        const drives = files.filter((f) => f.type === 'drive');
        const nodes: TreemapNode[] = [];
        for (let i = 0; i < drives.length; i++) {
          if (cancelled) return;
          const d = drives[i];
          const volume = d.path.slice(0, 2);
          let used = d.size;
          try {
            const vs = await (fs as any).getVolumeSize?.(volume);
            if (vs) used = vs.used_size ?? vs.total_size - vs.free_size;
          } catch (e) {
            console.error('[treemap] getVolumeSize failed', volume, e);
          }
          nodes.push({ name: d.name || volume, path: d.path, size: used, isDir: true });
          if (!cancelled) {
            setData((cur) => ({ ...cur, progress: Math.round(((i + 1) / Math.max(drives.length, 1)) * 100) }));
          }
        }
        if (cancelled) return;
        const { nodes: fin, total } = finalize(nodes);
        setData({ nodes: fin, total, loading: false, progress: 100 });
        return;
      }

      // Directory view — preferred path: one-shot server-sorted top-N.
      // The host returns the biggest children (dirs AND files, already mixed and
      // sorted) plus an aggregated "others", so a directory with thousands of
      // subfolders (e.g. WinSxS) costs ONE request instead of an RPC storm.
      if (typeof (fs as any).getChildrenSizes === 'function') {
        const sep = currentPath.endsWith('\\') ? '' : '\\';
        let res: { children: ChildSizeNode[]; total: number; otherSize: number; otherCount: number } | null = null;
        try {
          res = await (fs as any).getChildrenSizes(currentPath, TOP_N);
        } catch (e) {
          console.error('[treemap] getChildrenSizes failed', e);
        }
        if (cancelled) return;
        if (res) {
          const nodes: TreemapNode[] = (res.children ?? []).map((c) => ({
            name: c.name,
            path: `${currentPath}${sep}${c.name}`,
            size: c.size,
            isDir: c.isDir,
            fileCount: c.fileCount,
          }));
          if (res.otherCount > 0 && res.otherSize > 0) {
            nodes.push({
              name: `其他 (${res.otherCount})`,
              path: '__other__',
              size: res.otherSize,
              isDir: false,
              isOther: true,
            });
          }
          // Backend already sorted + truncated + summed; no front-end finalize().
          setData({ nodes, total: res.total, loading: false, progress: 100 });
          return;
        }
        // res === null → host rejected/unavailable, fall through to legacy path.
      }

      // Legacy fallback (older host without fs.getChildrenSizes):
      // files from the listing + per-folder sizes via batched getFolderSizes.
      const dirs = files.filter((f) => f.type === 'dir');
      const plainFiles = files.filter((f) => f.type === 'file');
      const nodes: TreemapNode[] = plainFiles.map((f) => ({
        name: f.name,
        path: f.path,
        size: f.size,
        isDir: false,
      }));

      const sizeMap: Record<string, FolderSizeResult> = {};
      for (let i = 0; i < dirs.length; i += BATCH) {
        if (cancelled) return;
        const batch = dirs.slice(i, i + BATCH);
        try {
          const res = await (fs as any).getFolderSizes(batch.map((f) => ({ path: f.path })));
          if (res) Object.assign(sizeMap, res);
        } catch (e) {
          console.error('[treemap] getFolderSizes failed', e);
        }
        if (!cancelled) {
          const done = Math.min(i + BATCH, dirs.length);
          setData((cur) => ({ ...cur, progress: Math.round((done / Math.max(dirs.length, 1)) * 100) }));
        }
      }
      if (cancelled) return;

      for (const d of dirs) {
        const fsz = sizeMap[d.path];
        nodes.push({
          name: d.name,
          path: d.path,
          size: fsz ? fsz.total_size : 0,
          isDir: true,
          fileCount: fsz?.file_count,
        });
      }

      if (cancelled) return;
      const { nodes: fin, total } = finalize(nodes);
      setData({ nodes: fin, total, loading: false, progress: 100 });
    };

    build();
    return () => {
      cancelled = true;
    };
  }, [currentPath, files, fs]);

  return data;
}
